import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getAnuncios, deleteAnuncio, createAnuncio, getComunidades, crearVotacion } from '../services/api';
import { useToast } from '../components/Toast';
import Pagination from '../components/Pagination';
import DateInput from '../components/DateInput';
import Button from '../components/ui/Button';
import EmptyState from '../components/EmptyState';
import ConfirmModal from '../components/ConfirmModal';
import './TablonAnuncios.css';

function TablonAnuncios({ user }) {
  const toast = useToast();
  const navigate = useNavigate();
  const [anuncios, setAnuncios] = useState([]);
  const [anunciosFiltrados, setAnunciosFiltrados] = useState([]);
  const [filtros, setFiltros] = useState({ tipo: [], fechaDesde: '', fechaHasta: '', busqueda: '', filtroComunidad: '' });
  const [loading, setLoading] = useState(true);
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);

  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [showModal, setShowModal] = useState(false);
  const [loadingSave, setLoadingSave] = useState(false);
  const [comunidades, setComunidades] = useState([]);
  const [formData, setFormData] = useState({ tipo: 'nota', titulo: '', contenido: '', archivo: null, comunidad_id: '' });
  const [votacionData, setVotacionData] = useState({ pregunta: '', tipo_votacion: 'simple', max_opciones: 1, opciones: ['', ''], fecha_cierre: '' });
  const [confirmModal, setConfirmModal] = useState({ isOpen: false, title: '', message: '', onConfirm: null });

  useEffect(() => {
    cargarAnuncios();
    if (user?.rol === 'administrador') {
      getComunidades().then(res => setComunidades(res.data)).catch(console.error);
    }
  }, []);

  useEffect(() => {
    aplicarFiltros();
  }, [anuncios, filtros]);

  const cargarAnuncios = async () => {
    try {
      const response = await getAnuncios();
      setAnuncios(response.data);
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const aplicarFiltros = () => {
    let resultado = [...anuncios];

    if (filtros.busqueda) {
      const busqueda = filtros.busqueda.toLowerCase();
      resultado = resultado.filter(a =>
        a.titulo.toLowerCase().includes(busqueda) ||
        a.contenido.toLowerCase().includes(busqueda)
      );
    }

    if (filtros.tipo.length > 0) {
      resultado = resultado.filter(a => filtros.tipo.includes(a.tipo));
    }

    if (filtros.fechaDesde) {
      const fechaDesde = new Date(filtros.fechaDesde);
      resultado = resultado.filter(a => new Date(a.fecha_creacion) >= fechaDesde);
    }

    if (filtros.fechaHasta) {
      const fechaHasta = new Date(filtros.fechaHasta);
      fechaHasta.setHours(23, 59, 59, 999);
      resultado = resultado.filter(a => new Date(a.fecha_creacion) <= fechaHasta);
    }

    // Filtro por comunidad (solo admin)
    if (filtros.filtroComunidad) {
      resultado = resultado.filter(a => String(a.comunidad_id) === String(filtros.filtroComunidad));
    }

    setAnunciosFiltrados(resultado);
    setCurrentPage(1);
  };

  const esVotacion = formData.tipo === 'votacion';

  const handleCloseModal = () => {
    setShowModal(false);
    setFormData({ tipo: 'nota', titulo: '', contenido: '', archivo: null, comunidad_id: '' });
    setVotacionData({ pregunta: '', tipo_votacion: 'simple', max_opciones: 1, opciones: ['', ''], fecha_cierre: '' });
  };

  const handleFormChange = (e) => {
    const { name, value, files } = e.target;
    setFormData({ ...formData, [name]: files ? files[0] : value });
  };

  const handleVotacionChange = (e) => {
    const { name, value } = e.target;
    setVotacionData({ ...votacionData, [name]: value, ...(name === 'tipo_votacion' && { max_opciones: value === 'simple' ? 1 : 2 }) });
  };

  const actualizarOpcion = (index, valor) => {
    const nuevas = [...votacionData.opciones];
    nuevas[index] = valor;
    setVotacionData({ ...votacionData, opciones: nuevas });
  };

  const agregarOpcion = () => setVotacionData({ ...votacionData, opciones: [...votacionData.opciones, ''] });

  const eliminarOpcion = (index) => {
    if (votacionData.opciones.length <= 2) return;
    setVotacionData({ ...votacionData, opciones: votacionData.opciones.filter((_, i) => i !== index) });
  };

  const handleSubmitAnuncio = async (e) => {
    e.preventDefault();
    if (esVotacion) {
      const limpias = votacionData.opciones.filter(o => o.trim());
      if (!votacionData.pregunta || limpias.length < 2) {
        toast.error('Para votación necesitas una pregunta y al menos 2 opciones.');
        return;
      }
    }
    setLoadingSave(true);
    try {
      const fd = new FormData();
      const tituloFinal = esVotacion ? votacionData.pregunta.trim() : formData.titulo;
      const contenidoFinal = esVotacion ? votacionData.pregunta.trim() : formData.contenido;
      fd.append('tipo', formData.tipo);
      fd.append('titulo', tituloFinal);
      fd.append('contenido', contenidoFinal);
      if (formData.comunidad_id) fd.append('comunidad_id', formData.comunidad_id);
      if (formData.archivo && !esVotacion) fd.append('archivo', formData.archivo);
      const respuesta = await createAnuncio(fd);
      if (esVotacion) {
        const limpias = votacionData.opciones.filter(o => o.trim());
        await crearVotacion({ anuncio_id: respuesta.data.id, pregunta: votacionData.pregunta, tipo_votacion: votacionData.tipo_votacion, max_opciones: votacionData.max_opciones, opciones: limpias, fecha_cierre: votacionData.fecha_cierre || null });
        toast.success('¡Anuncio y votación creados!');
      } else {
        toast.success('¡Anuncio publicado!');
      }
      handleCloseModal();
      cargarAnuncios();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Error al crear anuncio.');
    } finally {
      setLoadingSave(false);
    }
  };

  const openConfirm = (title, message, onConfirm) => {
    setConfirmModal({ isOpen: true, title, message, onConfirm });
  };
  const closeConfirm = () => setConfirmModal(prev => ({ ...prev, isOpen: false }));

  const handleEliminar = (id) => {
    openConfirm(
      'Eliminar anuncio',
      '¿Estás seguro de que quieres eliminar este anuncio? Esta acción no se puede deshacer.',
      async () => {
        closeConfirm();
        try {
          await deleteAnuncio(id);
          cargarAnuncios();
        } catch (error) {
          toast.error('Error al eliminar anuncio');
        }
      }
    );
  };

  const getTipoIcon = (tipo) => {
    const icons = {
      nota: '📌',
      comunicado: '📢',
      convocatoria: '📅',
      votacion: '🗳️'
    };
    return icons[tipo] || '📄';
  };

  const puedeGestionar = ['administrador', 'presidente'].includes(user.rol);

  const hasReadFlag = anuncios.some((a) => (
    typeof a.leido === 'boolean' || typeof a.read === 'boolean' || typeof a.visto === 'boolean'
  ));
  const unreadCount = hasReadFlag
    ? anuncios.filter((a) => !(a.leido ?? a.read ?? a.visto)).length
    : null;

  const hasPinned = anuncios.some((a) => a.fijado || a.pinned);
  const anunciosFijados = hasPinned
    ? anunciosFiltrados.filter((a) => a.fijado || a.pinned).slice(0, 3)
    : [];
  const anunciosFeed = hasPinned
    ? anunciosFiltrados.filter((a) => !(a.fijado || a.pinned))
    : anunciosFiltrados;

  const totalPages = Math.ceil(anunciosFeed.length / itemsPerPage);
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const anunciosPaginados = anunciosFeed.slice(indexOfFirstItem, indexOfLastItem);

  const formatFecha = (fecha) => {
    if (!fecha) return '';
    return new Date(fecha).toLocaleString('es-ES', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleCardClick = (id) => {
    navigate(`/tablon/${id}`);
  };

  const handleCardKeyDown = (event, id) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      handleCardClick(id);
    }
  };

  return (
    <div className="tablon-page">
      <header className="tablon-header">
        <div>
          <h1 className="tablon-title">📢 Tablón</h1>
          <p className="tablon-subtitle">Avisos y comunicaciones de la comunidad</p>
        </div>
        <div className="tablon-header-actions">
          {hasReadFlag && (
            <span className="tablon-unread">No leidos: {unreadCount}</span>
          )}
          {puedeGestionar && (
            <Button variant="primary" size="md" onClick={() => setShowModal(true)}>Nuevo aviso</Button>
          )}
        </div>
      </header>

      <section className="tablon-filters">
        <div className="tablon-search">
          <span className="tablon-search-icon" aria-hidden="true">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="11" cy="11" r="8"></circle>
              <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
            </svg>
          </span>
          <input
            type="text"
            className="form-control tablon-search-input"
            placeholder="Buscar por titulo o contenido..."
            value={filtros.busqueda}
            onChange={(e) => setFiltros({ ...filtros, busqueda: e.target.value })}
          />
        </div>

        <div className="tablon-filter-row">
          <div className="tablon-filter">
            <label className="form-label">Tipo</label>
            <select
              className="form-control"
              value={filtros.tipo.length > 0 ? filtros.tipo[0] : ''}
              onChange={(e) => setFiltros({ ...filtros, tipo: e.target.value ? [e.target.value] : [] })}
            >
              <option value="">Todos</option>
              <option value="nota">📝 Nota</option>
              <option value="comunicado">📢 Comunicado</option>
              <option value="convocatoria">📅 Convocatoria</option>
              <option value="votacion">🗳️ Votación</option>
            </select>
          </div>
          <button
            className="tablon-filter-advanced-toggle"
            onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
          >
            {showAdvancedFilters ? 'Básico' : 'Más filtros'}
          </button>
        </div>

        {showAdvancedFilters && (
          <div className="tablon-advanced-filters">
            <div className="tablon-filter-dates">
              <div className="tablon-filter">
                <label className="form-label">Desde</label>
                <DateInput
                  value={filtros.fechaDesde}
                  onChange={(val) => setFiltros({ ...filtros, fechaDesde: val })}
                  placeholder="dd-mm-aaaa"
                />
              </div>
              <div className="tablon-filter">
                <label className="form-label">Hasta</label>
                <DateInput
                  value={filtros.fechaHasta}
                  onChange={(val) => setFiltros({ ...filtros, fechaHasta: val })}
                  placeholder="dd-mm-aaaa"
                />
              </div>
              {user?.rol === 'administrador' && comunidades.length > 0 && (
                <div className="tablon-filter">
                  <label className="form-label">Comunidad</label>
                  <select
                    className="form-control"
                    value={filtros.filtroComunidad}
                    onChange={(e) => setFiltros({ ...filtros, filtroComunidad: e.target.value })}
                  >
                    <option value="">Todas</option>
                    {comunidades.map(c => (
                      <option key={c.id} value={c.id}>{c.nombre}</option>
                    ))}
                  </select>
                </div>
              )}
            </div>
          </div>
        )}

        {(filtros.busqueda || filtros.tipo.length > 0 || filtros.fechaDesde || filtros.fechaHasta || filtros.filtroComunidad) && (
          <div className="tablon-filter-summary">
            <div className="tablon-results">
              <strong>{anunciosFiltrados.length}</strong> {anunciosFiltrados.length === 1 ? 'resultado' : 'resultados'}
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => { setFiltros({ tipo: [], fechaDesde: '', fechaHasta: '', busqueda: '', filtroComunidad: '' }); setShowAdvancedFilters(false); }}
            >
              Limpiar filtros
            </Button>
          </div>
        )}
      </section>

      {loading ? (
        <div className="tablon-skeleton-list">
          {[1, 2, 3, 4, 5].map((item) => (
            <div key={item} className="tablon-skeleton"></div>
          ))}
        </div>
      ) : anunciosFiltrados.length === 0 ? (
        <EmptyState
          icon="📢"
          title={filtros.busqueda || filtros.tipo.length > 0 || filtros.fechaDesde || filtros.fechaHasta || filtros.filtroComunidad
            ? 'No se encontraron avisos'
            : 'No hay avisos nuevos'}
          message={filtros.busqueda || filtros.tipo.length > 0 || filtros.fechaDesde || filtros.fechaHasta || filtros.filtroComunidad
            ? 'Intenta ajustar los filtros para ver más avisos'
            : 'Cuando haya nuevos comunicados aparecerán aquí'}
          action={
            puedeGestionar && (
              <Button variant="primary" size="md" onClick={() => setShowModal(true)}>
                + Publicar Aviso
              </Button>
            )
          }
        />
      ) : (
        <>
          {hasPinned && anunciosFijados.length > 0 && (
            <section className="tablon-section">
              <div className="tablon-section-title">Fijados</div>
              <div className="tablon-feed">
                {anunciosFijados.map((anuncio) => {
                  const isRead = hasReadFlag ? (anuncio.leido ?? anuncio.read ?? anuncio.visto) : null;
                  return (
                    <article
                      key={anuncio.id}
                      className={`tablon-card tablon-card--pinned ${isRead ? 'tablon-card--read' : 'tablon-card--unread'}`}
                      onClick={() => handleCardClick(anuncio.id)}
                      onKeyDown={(event) => handleCardKeyDown(event, anuncio.id)}
                      role="button"
                      tabIndex={0}
                    >
                      <div className="tablon-card-header">
                        <div className="tablon-card-title">
                          <span className="tablon-pin">📌</span>
                          {anuncio.titulo}
                        </div>
                        {!isRead && hasReadFlag && <span className="tablon-new-dot"></span>}
                      </div>
                      <div className="tablon-card-meta">
                        {anuncio.tipo && (
                          <span className={`tablon-tag tablon-tag--${anuncio.tipo}`}>
                            {getTipoIcon(anuncio.tipo)} {anuncio.tipo}
                          </span>
                        )}
                        <span className="tablon-date">{formatFecha(anuncio.fecha_creacion)}</span>
                      </div>
                      <p className="tablon-extract">{anuncio.contenido}</p>
                      <div className="tablon-card-footer">
                        <div className="tablon-meta">
                          <span>👤 {anuncio.autor_nombre}</span>
                          {anuncio.archivo && <span>📎 Archivo</span>}
                        </div>
                        <div className="tablon-actions">
                          <Button
                            variant="secondary"
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleCardClick(anuncio.id);
                            }}
                          >
                            Ver detalle
                          </Button>
                          {puedeGestionar && (
                            <Button
                              variant="danger"
                              size="sm"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleEliminar(anuncio.id);
                              }}
                            >
                              Eliminar
                            </Button>
                          )}
                        </div>
                      </div>
                    </article>
                  );
                })}
              </div>
            </section>
          )}

          <section className="tablon-section">
            {hasPinned && anunciosFijados.length > 0 && <div className="tablon-section-title">Todos</div>}
            <div className="tablon-feed">
              {anunciosPaginados.map((anuncio) => {
                const isRead = hasReadFlag ? (anuncio.leido ?? anuncio.read ?? anuncio.visto) : null;
                return (
                  <article
                    key={anuncio.id}
                    className={`tablon-card ${isRead ? 'tablon-card--read' : 'tablon-card--unread'}`}
                    onClick={() => handleCardClick(anuncio.id)}
                    onKeyDown={(event) => handleCardKeyDown(event, anuncio.id)}
                    role="button"
                    tabIndex={0}
                  >
                    <div className="tablon-card-header">
                      <div className="tablon-card-title">{anuncio.titulo}</div>
                      {!isRead && hasReadFlag && <span className="tablon-new-dot"></span>}
                    </div>
                    <div className="tablon-card-meta">
                      {anuncio.tipo && (
                        <span className={`tablon-tag tablon-tag--${anuncio.tipo}`}>
                          {getTipoIcon(anuncio.tipo)} {anuncio.tipo}
                        </span>
                      )}
                      <span className="tablon-date">{formatFecha(anuncio.fecha_creacion)}</span>
                    </div>
                    <p className="tablon-extract">{anuncio.contenido}</p>
                    <div className="tablon-card-footer">
                      <div className="tablon-meta">
                        <span>👤 {anuncio.autor_nombre}</span>
                        {anuncio.archivo && <span>📎 Archivo</span>}
                      </div>
                      <div className="tablon-actions">
                        <Button
                          variant="secondary"
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleCardClick(anuncio.id);
                          }}
                        >
                          Ver detalle
                        </Button>
                        {puedeGestionar && (
                          <Button
                            variant="danger"
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleEliminar(anuncio.id);
                            }}
                          >
                            Eliminar
                          </Button>
                        )}
                      </div>
                    </div>
                  </article>
                );
              })}
            </div>
          </section>

          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            itemsPerPage={itemsPerPage}
            totalItems={anunciosFeed.length}
            onPageChange={setCurrentPage}
            onItemsPerPageChange={setItemsPerPage}
          />
        </>
      )}
      {showModal && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(15, 23, 42, 0.5)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1000, backdropFilter: 'blur(8px)' }} onClick={handleCloseModal}>
          <div style={{ background: '#ffffff', borderRadius: 'var(--radius-xl)', padding: 'var(--space-6)', maxWidth: '560px', width: '90%', boxShadow: '0 20px 60px rgba(0,0,0,0.3)', maxHeight: '90vh', overflow: 'auto' }} onClick={(e) => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 'var(--space-4)' }}>
              <h2 style={{ fontSize: 'var(--font-2xl)', fontWeight: '700', margin: 0 }}>Nuevo aviso</h2>
              <button onClick={handleCloseModal} style={{ background: 'none', border: 'none', fontSize: '24px', cursor: 'pointer' }}>✖️</button>
            </div>
            <form onSubmit={handleSubmitAnuncio}>
              <div style={{ marginBottom: 'var(--space-4)' }}>
                <label className="form-label">Tipo *</label>
                <select name="tipo" className="form-control" value={formData.tipo} onChange={handleFormChange} required>
                  <option value="nota">📌 Nota</option>
                  <option value="comunicado">📢 Comunicado</option>
                  <option value="convocatoria">📅 Convocatoria</option>
                  <option value="votacion">📊 Votación</option>
                </select>
              </div>
              {user?.rol === 'administrador' && (
                <div style={{ marginBottom: 'var(--space-4)' }}>
                  <label className="form-label">Comunidad *</label>
                  <select name="comunidad_id" className="form-control" value={formData.comunidad_id} onChange={handleFormChange} required>
                    <option value="">Selecciona una comunidad</option>
                    {comunidades.map(com => (<option key={com.id} value={com.id}>{com.nombre}</option>))}
                  </select>
                </div>
              )}
              {!esVotacion && (
                <>
                  <div style={{ marginBottom: 'var(--space-4)' }}>
                    <label className="form-label">Título *</label>
                    <input type="text" name="titulo" className="form-control" value={formData.titulo} onChange={handleFormChange} required placeholder="Ej: Reunión de vecinos" />
                  </div>
                  <div style={{ marginBottom: 'var(--space-4)' }}>
                    <label className="form-label">Contenido *</label>
                    <textarea name="contenido" className="form-control" value={formData.contenido} onChange={handleFormChange} required rows="5" placeholder="Escribe el contenido del anuncio..." />
                  </div>
                  <div style={{ marginBottom: 'var(--space-4)' }}>
                    <label className="form-label">Archivo adjunto (opcional)</label>
                    <input type="file" name="archivo" className="form-control" onChange={handleFormChange} accept=".pdf,.doc,.docx,.jpg,.jpeg,.png" />
                  </div>
                </>
              )}
              {esVotacion && (
                <div style={{ padding: '16px', background: 'var(--bg-primary)', borderRadius: '8px', marginBottom: 'var(--space-4)' }}>
                  <div style={{ marginBottom: 'var(--space-3)' }}>
                    <label className="form-label">Pregunta *</label>
                    <input type="text" name="pregunta" className="form-control" value={votacionData.pregunta} onChange={handleVotacionChange} placeholder="¿Aprobamos el presupuesto?" required />
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: 'var(--space-3)' }}>
                    <div>
                      <label className="form-label">Tipo</label>
                      <select name="tipo_votacion" className="form-control" value={votacionData.tipo_votacion} onChange={handleVotacionChange}>
                        <option value="simple">Simple (1 opción)</option>
                        <option value="multiple">Múltiple</option>
                      </select>
                    </div>
                    <div>
                      <label className="form-label">Fecha cierre</label>
                      <input type="date" name="fecha_cierre" className="form-control" value={votacionData.fecha_cierre} onChange={handleVotacionChange} />
                    </div>
                  </div>
                  <div>
                    <label className="form-label">Opciones *</label>
                    {votacionData.opciones.map((opcion, index) => (
                      <div key={index} style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
                        <input type="text" className="form-control" value={opcion} onChange={(e) => actualizarOpcion(index, e.target.value)} placeholder={`Opción ${index + 1}`} required={index < 2} />
                        {votacionData.opciones.length > 2 && (
                          <button type="button" onClick={() => eliminarOpcion(index)} style={{ background: 'none', border: '1px solid var(--border-color)', borderRadius: '8px', padding: '0 12px', cursor: 'pointer' }}>✕</button>
                        )}
                      </div>
                    ))}
                    <button type="button" onClick={agregarOpcion} style={{ background: 'none', border: '1px dashed var(--border-color)', borderRadius: '8px', padding: '8px', cursor: 'pointer', width: '100%', color: 'var(--text-secondary)', fontSize: '14px' }}>+ Añadir opción</button>
                  </div>
                </div>
              )}
              <div style={{ display: 'flex', gap: 'var(--space-3)' }}>
                <Button variant="primary" type="submit" disabled={loadingSave} style={{ flex: 1 }}>{loadingSave ? 'Publicando...' : 'Publicar'}</Button>
                <Button variant="ghost" onClick={handleCloseModal} style={{ flex: 1 }}>Cancelar</Button>
              </div>
            </form>
          </div>
        </div>
      )}
      <ConfirmModal
        isOpen={confirmModal.isOpen}
        title={confirmModal.title}
        message={confirmModal.message}
        onConfirm={confirmModal.onConfirm}
        onCancel={closeConfirm}
        confirmLabel="Eliminar"
        variant="danger"
      />
    </div>
  );
}

export default TablonAnuncios;
